/**
 * Runtime Configuration
 *
 * Fetches configuration from the backend that may change between deployments
 * without requiring a frontend rebuild. This is essential for features like
 * the Cloudflare upload bypass URL.
 */

import { api } from './api'

export interface RuntimeConfig {
  version: string
  environment: string
  uploadBaseUrl: string
  baseDomain: string
}

// Singleton to cache the config
let cachedConfig: RuntimeConfig | null = null
let configPromise: Promise<RuntimeConfig> | null = null

/**
 * Fetch runtime configuration from the backend.
 * Results are cached for the lifetime of the page.
 */
export async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig
  }

  // If a fetch is already in progress, wait for it
  if (configPromise) {
    return configPromise
  }

  // Start fetching
  configPromise = api
    .get('/health/config')
    .then((response) => {
      // Note: API interceptor transforms snake_case to camelCase,
      // so upload_base_url becomes uploadBaseUrl
      cachedConfig = {
        version: response.data.version,
        environment: response.data.environment,
        uploadBaseUrl: response.data.uploadBaseUrl || '',
        baseDomain: response.data.baseDomain || '',
      }
      return cachedConfig
    })
    .catch((error) => {
      console.warn('Failed to fetch runtime config, using defaults:', error)
      // Return defaults on error
      cachedConfig = {
        version: 'unknown',
        environment: 'unknown',
        uploadBaseUrl: '',
        baseDomain: '',
      }
      return cachedConfig
    })
    .finally(() => {
      // Clear the promise so future calls can retry if needed
      configPromise = null
    })

  return configPromise
}

/**
 * Get the cached runtime config synchronously.
 * Returns null if config hasn't been fetched yet.
 * Use fetchRuntimeConfig() to ensure config is loaded.
 */
export function getRuntimeConfig(): RuntimeConfig | null {
  return cachedConfig
}

/**
 * Get the upload base URL for large file uploads.
 * Returns empty string if not configured (uses default API).
 */
export function getUploadBaseUrl(): string {
  // First check build-time env var (for backwards compatibility)
  const buildTimeUrl = import.meta.env.VITE_UPLOAD_BASE_URL
  if (buildTimeUrl) {
    return buildTimeUrl.replace(/\/$/, '')
  }

  // Then check runtime config
  if (cachedConfig?.uploadBaseUrl) {
    return cachedConfig.uploadBaseUrl.replace(/\/$/, '')
  }

  return ''
}
